import * as Sentry from "@sentry/react";
import packageJson from "../package.json";

const {
  REACT_APP_SENTRY_DSN,
  REACT_APP_SENTRY_ENVIRONMENT,
  REACT_APP_SENTRY_ENABLED,
  REACT_APP_BRAND_NAME,
  REACT_APP_LOG_LEVEL,
  NODE_ENV,
} = process.env;

// Log level priority (higher number = higher priority)
const LOG_LEVEL_PRIORITY = {
  trace: 0,
  debug: 1,
  info: 2,
  warn: 3,
  error: 4,
  fatal: 5,
};

// Default log level: 'debug' in development, 'info' in production
const DEFAULT_LOG_LEVEL = NODE_ENV === "development" ? "debug" : "info";
const minLogLevel = REACT_APP_LOG_LEVEL || DEFAULT_LOG_LEVEL;

export function initSentry() {
  // Only initialize Sentry if DSN is provided and not explicitly disabled
  if (!REACT_APP_SENTRY_DSN) {
    console.warn("Sentry DSN not configured. Error tracking is disabled.");
    return;
  }

  if (REACT_APP_SENTRY_ENABLED === "false") {
    console.warn(
      "Sentry explicitly disabled via REACT_APP_SENTRY_ENABLED=false"
    );
    return;
  }

  Sentry.init({
    dsn: REACT_APP_SENTRY_DSN,
    release: `portal-owners@${packageJson.version}`,
    environment: REACT_APP_SENTRY_ENVIRONMENT || NODE_ENV || "development",

    // Enable Sentry debug logging in development (shows what Sentry is doing)
    debug: NODE_ENV === "development",

    // Enable structured logging (required for Sentry.logger API)
    enableLogs: true,

    // Set tracesSampleRate to 1.0 to capture 100% of transactions for performance monitoring.
    // We recommend adjusting this value in production (0.1 = 10%)
    // All sessions containing errors will be recorded regardless of this setting
    tracesSampleRate: 0,

    // Integrations
    integrations: [
      // React Router integration for better error tracking
      Sentry.browserTracingIntegration(),
      // Replay integration for session replay (optional)
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
      // Capture console logs (only in development for debugging)
      ...(NODE_ENV === "development"
        ? [
            Sentry.captureConsoleIntegration({
              levels: ["log", "info", "warn", "error", "debug"],
            }),
          ]
        : []),
    ],

    // Session Replay sample rates
    // Only record sessions when errors occur (buffer mode)
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0, // Always capture replays when errors occur

    // Filter logs based on REACT_APP_LOG_LEVEL
    beforeSendLog: (log) => {
      const minPriority =
        LOG_LEVEL_PRIORITY[minLogLevel] ?? LOG_LEVEL_PRIORITY.info;
      const logPriority = LOG_LEVEL_PRIORITY[log.level] ?? 0;

      // Filter out logs below minimum level
      if (logPriority < minPriority) {
        return null;
      }

      // In development, also output to browser console for visibility
      if (NODE_ENV === "development") {
        const consoleMethod =
          log.level === "fatal" || log.level === "error"
            ? "error"
            : log.level === "warn"
              ? "warn"
              : log.level === "info"
                ? "info"
                : "debug";
        console[consoleMethod](
          `[Sentry ${log.level}]`,
          log.message,
          log.attributes
        );
      }

      return log;
    },

    // Filter out specific errors
    beforeSend(event, hint) {
      const errorValue = event.exception?.values?.[0]?.value || "";
      const errorType = event.exception?.values?.[0]?.type || "";

      // Don't send events for browser extensions
      if (errorValue.includes("Extension context invalidated")) {
        return null;
      }

      // Don't send network errors (transient, not actionable)
      if (
        errorType === "AxiosError" &&
        (errorValue.includes("Network Error") ||
          errorValue.includes("timeout") ||
          errorValue.includes("ECONNABORTED"))
      ) {
        return null;
      }

      return event;
    },

    // Add custom tags
    initialScope: {
      tags: {
        brand: REACT_APP_BRAND_NAME || "unknown",
      },
    },
  });

  console.info(`Sentry initialized successfully (log level: ${minLogLevel})`);
}
